"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { SlidersHorizontal, LayoutGrid, AlertTriangle } from "lucide-react";
import { Badge, Button, Input, Label } from "@eg/ui";
import {
  buildFacets,
  filterProductsByState,
  fetchEnrichedProducts,
  normalizeCategoryParam,
  type CategoryId,
  type DiscoveryFilters,
  type EnrichedProduct,
} from "@/lib/discovery";
import { FilterSidebar } from "@/components/discovery/FilterSidebar";
import { ProductCard } from "@/components/discovery/ProductCard";
import { QuickViewModal } from "@/components/discovery/QuickViewModal";
import { useLikesStore } from "@/store/likes";
import { useTranslations } from "@/lib/i18n/I18nProvider";

type Props = {
  initialQuery?: string;
};

function parseFilters(
  params: URLSearchParams,
): DiscoveryFilters & { highlight?: string } {
  const parseList = (key: string) => {
    const values = params.getAll(key);
    if (values.length) return values;
    const singular = params.get(key);
    return singular ? [singular] : [];
  };

  const priceMin = params.get("priceMin");
  const priceMax = params.get("priceMax");

  return {
    q: params.get("q") ?? "",
    categories: parseList("category")
      .map((value) => normalizeCategoryParam(value))
      .filter(Boolean) as CategoryId[],
    brands: parseList("brand"),
    badges: parseList("badge"),
    availability:
      (params.get("availability") as DiscoveryFilters["availability"]) ?? "all",
    priceMin:
      priceMin && !Number.isNaN(Number(priceMin))
        ? Number(priceMin)
        : undefined,
    priceMax:
      priceMax && !Number.isNaN(Number(priceMax))
        ? Number(priceMax)
        : undefined,
    sort: (params.get("sort") as DiscoveryFilters["sort"]) ?? "newest",
    highlight: params.get("highlight") ?? undefined,
  };
}

export function DiscoveryClient({ initialQuery }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const parsed = useMemo(() => parseFilters(searchParams), [searchParams]);
  const t = useTranslations();

  const categoryLabel = useMemo(() => {
    const map: Record<CategoryId, string> = {
      vaporizer: t("categories.vaporizer"),
      grinder: t("categories.grinder"),
      papers: t("categories.papers"),
      filterTips: t("categories.filterTips"),
      cones: t("categories.cones"),
      lighters: t("categories.lighters"),
      storage: t("categories.storage"),
      accessories: t("categories.accessories"),
      cleaning: t("categories.cleaning"),
      scales: t("categories.scales"),
      humidor: t("categories.humidor"),
      assortment: t("categories.assortment"),
    };
    return (id: CategoryId) => map[id] ?? id;
  }, [t]);

  const [searchDraft, setSearchDraft] = useState(
    parsed.q ?? initialQuery ?? "",
  );
  const [products, setProducts] = useState<EnrichedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickView, setQuickView] = useState<EnrichedProduct | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const likedIds = useLikesStore((s) => s.likedIds);
  const likesInitialized = useLikesStore((s) => s.initialized);
  const setLikeCount = useLikesStore((s) => s.setCount);

  useEffect(() => {
    setSearchDraft(parsed.q ?? "");
  }, [parsed.q]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchEnrichedProducts(parsed.q ?? undefined, 64)
      .then((res) => {
        if (!active) return;
        setProducts(res);
      })
      .catch((e: unknown) => {
        if (!active) return;
        setError(
          e instanceof Error ? e.message : t("discovery.loadErrorFallback"),
        );
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [parsed.q, t]);

  useEffect(() => {
    products.forEach((p) => setLikeCount(p.id, p.likesCount ?? 0));
  }, [products, setLikeCount]);

  const facets = useMemo(() => buildFacets(products), [products]);
  const filtered = useMemo(() => {
    const wantsLiked =
      parsed.badges.includes("wishlist") || parsed.badges.includes("liked");
    const effectiveBadges = parsed.badges.filter(
      (b) => b !== "wishlist" && b !== "liked",
    );
    const base = filterProductsByState(products, {
      ...parsed,
      badges: effectiveBadges,
    });

    if (!wantsLiked) return base;
    if (!likesInitialized) return [];

    return base.filter((p) => likedIds.has(p.id));
  }, [parsed, products, likedIds, likesInitialized]);

  const updateFilters = useCallback(
    (next: Partial<DiscoveryFilters>) => {
      const merged: DiscoveryFilters = {
        ...parsed,
        ...next,
      };
      const params = new URLSearchParams();
      if (merged.q) params.set("q", merged.q);
      if (merged.categories.length)
        merged.categories.forEach((c) => params.append("category", c));
      if (merged.brands.length)
        merged.brands.forEach((b) => params.append("brand", b));
      if (merged.badges.length)
        merged.badges.forEach((b) => params.append("badge", b));
      if (merged.availability !== "all")
        params.set("availability", merged.availability);
      if (typeof merged.priceMin === "number")
        params.set("priceMin", `${merged.priceMin}`);
      if (typeof merged.priceMax === "number")
        params.set("priceMax", `${merged.priceMax}`);
      if (merged.sort && merged.sort !== "newest")
        params.set("sort", merged.sort);
      if (parsed.highlight) params.set("highlight", parsed.highlight);
      router.replace(`/produkte?${params.toString()}`, { scroll: false });
    },
    [parsed, router],
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchDraft !== parsed.q) {
        updateFilters({ q: searchDraft });
      }
    }, 320);
    return () => clearTimeout(handle);
  }, [parsed.q, searchDraft, updateFilters]);

  const resetFilters = () => {
    updateFilters({
      categories: [],
      brands: [],
      badges: [],
      availability: "all",
      priceMin: undefined,
      priceMax: undefined,
      sort: "newest",
    });
  };

  return (
    <div className="space-y-8">
      <header className="rounded-3xl border border-slate-200 bg-slate-50/95 p-6 shadow-sm backdrop-blur">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
              {t("discovery.titleEyebrow")}
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              {t("discovery.title")}
            </h1>
            <p className="text-sm text-slate-700">{t("discovery.subtitle")}</p>
          </div>
          <div className="flex flex-1 items-center gap-3 md:max-w-xl">
            <Input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder={t("discovery.searchPlaceholder")}
              className="h-11 rounded-xl border-slate-300 bg-white pl-4 text-slate-900 placeholder:text-slate-500 shadow-sm focus-visible:border-cyan-400 focus-visible:ring-2 focus-visible:ring-cyan-300"
            />
            <Badge
              variant="outline"
              className="hidden whitespace-nowrap border-slate-300 bg-white text-slate-700 md:inline-flex"
            >
              {t("discovery.hits", { count: filtered.length })}
            </Badge>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            {parsed.q ? (
              <Badge
                variant="outline"
                className="border-slate-300 bg-white text-slate-700"
              >
                {t("discovery.searchBadge", { q: parsed.q })}
              </Badge>
            ) : null}
            {parsed.categories.map((c) => (
              <Badge
                key={c}
                variant="outline"
                className="border-cyan-300 bg-cyan-50 text-cyan-900"
              >
                {categoryLabel(c)}
              </Badge>
            ))}
            {parsed.brands.map((b) => (
              <Badge
                key={b}
                variant="outline"
                className="border-slate-300 bg-white text-slate-700"
              >
                {b}
              </Badge>
            ))}
            {parsed.badges.map((b) => (
              <Badge
                key={b}
                variant="outline"
                className="capitalize border-slate-300 bg-white text-slate-700"
              >
                {b}
              </Badge>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Label className="text-xs uppercase tracking-wide text-slate-600">
              {t("discovery.sortLabel")}
            </Label>
            <select
              value={parsed.sort ?? "newest"}
              onChange={(e) =>
                updateFilters({
                  sort: e.target.value as DiscoveryFilters["sort"],
                })
              }
              className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-cyan-300"
            >
              <option value="newest">{t("discovery.sort.newest")}</option>
              <option value="price-asc">{t("discovery.sort.priceAsc")}</option>
              <option value="price-desc">
                {t("discovery.sort.priceDesc")}
              </option>
              <option value="popular">{t("discovery.sort.popular")}</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-800 hover:bg-slate-100 md:hidden"
              onClick={() => setMobileFiltersOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t("discovery.filterButton")}
            </Button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px,1fr]">
        <div className="hidden lg:block">
          <FilterSidebar
            filters={parsed}
            facets={facets}
            onChange={updateFilters}
            onReset={resetFilters}
          />
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div
                  key={idx}
                  className="animate-pulse rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <div className="mb-3 h-40 rounded-xl bg-gray-100" />
                  <div className="mb-2 h-4 w-3/4 rounded bg-gray-100" />
                  <div className="mb-4 h-3 w-2/3 rounded bg-gray-100" />
                  <div className="h-5 w-1/2 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-amber-200 bg-amber-50 p-8 text-amber-800">
              <AlertTriangle className="h-8 w-8" />
              <p className="text-lg font-semibold">
                {t("discovery.loadErrorTitle")}
              </p>
              <p className="text-sm">{error}</p>
              <Button onClick={() => updateFilters({ q: parsed.q })}>
                {t("common.retry")}
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-gray-100 bg-white/80 p-10 text-center shadow-sm">
              <LayoutGrid className="h-10 w-10 text-gray-300" />
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-900">
                  {t("discovery.emptyTitle")}
                </p>
                <p className="text-sm text-slate-700">
                  {t("discovery.emptyCopy")}
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <Button variant="outline" onClick={resetFilters}>
                  {t("discovery.resetFilters")}
                </Button>
                {parsed.q ? (
                  <Button
                    variant="ghost"
                    onClick={() => updateFilters({ q: "" })}
                  >
                    {t("discovery.clearSearch")}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {filtered.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  highlight={parsed.highlight === product.slug}
                  onQuickView={setQuickView}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {mobileFiltersOpen ? (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden">
          <div className="absolute inset-y-0 right-0 w-full max-w-md overflow-auto bg-white p-5 shadow-xl">
            <FilterSidebar
              filters={parsed}
              facets={facets}
              onChange={updateFilters}
              onReset={resetFilters}
              onClose={() => setMobileFiltersOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <QuickViewModal product={quickView} onClose={() => setQuickView(null)} />
    </div>
  );
}
